import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  API_KEY_SCOPES,
  WEBHOOK_EVENT_TYPES,
  WEBHOOK_EVENT_LABELS,
  type WebhookEventType,
} from '@marketing-hub/shared';
import { apiFetch } from '@/api/client';
import { useProjectContext } from '@/project/ProjectContext';
import { ProjectGate } from '@/features/projects/ProjectGate';
import { Button } from '@/components/ui/Button';
import { Field, TextInput } from '@/components/ui/Field';
import { LoadingState, StateMessage } from '@/components/StateMessage/StateMessage';
import { TraceLink } from '@/features/traces/TraceLink';
import styles from './ProjectsPage.module.css';
export function DeveloperPage() {
  const { selectedProject } = useProjectContext();
  const projectId = selectedProject?.id;
  const qc = useQueryClient();

  const [keyName, setKeyName] = useState('Zapier');
  const [keyScopes, setKeyScopes] = useState<string[]>(['leads:read', 'leads:write']);
  const [plainKey, setPlainKey] = useState<string | null>(null);

  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEvents, setWebhookEvents] = useState<WebhookEventType[]>(['lead.created']);
  const [webhookSecret, setWebhookSecret] = useState<string | null>(null);

  const [domain, setDomain] = useState('');

  const keysQ = useQuery({
    queryKey: ['dev-keys', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{ keys: { id: string; name: string; key_prefix: string; scopes: string[] }[] }>(
        `/api/developer/api-keys?project_id=${projectId}`,
      ),
  });

  const hooksQ = useQuery({
    queryKey: ['dev-webhooks', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{ webhooks: { id: string; url: string; events: string[]; enabled: boolean }[] }>(
        `/api/developer/webhooks?project_id=${projectId}`,
      ),
  });

  const deliveriesQ = useQuery({
    queryKey: ['dev-deliveries', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{
        deliveries: {
          id: string;
          event_type: string;
          status: string;
          attempts: number;
          response_status: number | null;
          created_at: string;
          trace_id: string | null;
          webhook_url: string;
        }[];
      }>(`/api/developer/webhook-deliveries?project_id=${projectId}`),
  });

  const domainsQ = useQuery({
    queryKey: ['dev-domains', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      apiFetch<{ domains: { id: string; domain_normalized: string }[] }>(
        `/api/developer/domains?project_id=${projectId}`,
      ),
  });

  const createKey = useMutation({
    mutationFn: () =>
      apiFetch<{ plaintext: string }>('/api/developer/api-keys', {
        method: 'POST',
        body: JSON.stringify({ project_id: projectId, name: keyName, scopes: keyScopes }),
      }),
    onSuccess: (data) => {
      setPlainKey(data.plaintext);
      void qc.invalidateQueries({ queryKey: ['dev-keys', projectId] });
    },
  });

  const createHook = useMutation({
    mutationFn: () =>
      apiFetch<{ signing_secret: string }>('/api/developer/webhooks', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          url: webhookUrl,
          events: webhookEvents,
        }),
      }),
    onSuccess: (data) => {
      setWebhookSecret(data.signing_secret);
      setWebhookUrl('');
      void qc.invalidateQueries({ queryKey: ['dev-webhooks', projectId] });
    },
  });

  const addDomain = useMutation({
    mutationFn: () =>
      apiFetch('/api/developer/domains', {
        method: 'POST',
        body: JSON.stringify({ project_id: projectId, domain }),
      }),
    onSuccess: () => {
      setDomain('');
      void qc.invalidateQueries({ queryKey: ['dev-domains', projectId] });
    },
  });

  const testHook = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/developer/webhooks/${id}/test`, { method: 'POST', body: '{}' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['dev-deliveries', projectId] }),
  });

  const revokeKey = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/developer/api-keys/${id}/revoke`, { method: 'POST', body: '{}' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['dev-keys', projectId] }),
  });

  return (
    <ProjectGate requireSelection pageTitle="Developer">
      {!projectId ? null : (
        <div className={styles.grid}>
          <StateMessage title="Developer portal (CRM I)">
            Public API <code>/api/v1</code> met scoped keys (prefix <code>mhak_</code>), outbound
            webhooks met HMAC, delivery log, multi-domain map. OpenAPI:{' '}
            <a href="/api/v1/openapi.json" target="_blank" rel="noreferrer">
              /api/v1/openapi.json
            </a>
            . <Link to="/app/integraties">Integraties →</Link>
          </StateMessage>

          <section className={styles.card}>
            <h2>API keys</h2>
            <form
              className={styles.formGrid}
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                createKey.mutate();
              }}
            >
              <Field label="Naam">
                <TextInput value={keyName} onChange={(e) => setKeyName(e.target.value)} required />
              </Field>
              <Field label="Scopes">
                {API_KEY_SCOPES.map((s) => (
                  <label key={s} className={styles.mutedSmall}>
                    <input
                      type="checkbox"
                      checked={keyScopes.includes(s)}
                      onChange={(e) => {
                        setKeyScopes((prev) =>
                          e.target.checked ? [...prev, s] : prev.filter((x) => x !== s),
                        );
                      }}
                    />{' '}
                    {s}
                  </label>
                ))}
              </Field>
              <Button type="submit">Key aanmaken</Button>
            </form>
            {plainKey ? (
              <p className={styles.mutedSmall}>
                <strong>Bewaar deze key — wordt niet opnieuw getoond:</strong> {plainKey}
              </p>
            ) : null}
            <ul className={styles.list}>
              {(keysQ.data?.keys ?? []).map((k) => (
                <li key={k.id} className={styles.listItem}>
                  {k.name} ({k.key_prefix}…) — {k.scopes.join(', ')}
                  <Button variant="ghost" onClick={() => revokeKey.mutate(k.id)}>
                    Revoke
                  </Button>
                </li>
              ))}
            </ul>
          </section>

          <section className={styles.card}>
            <h2>Outbound webhooks</h2>
            <form
              className={styles.formGrid}
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                createHook.mutate();
              }}
            >
              <Field label="URL">
                <TextInput
                  type="url"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  required
                />
              </Field>
              <Field label="Events">
                {WEBHOOK_EVENT_TYPES.map((ev) => (
                  <label key={ev} className={styles.mutedSmall}>
                    <input
                      type="checkbox"
                      checked={webhookEvents.includes(ev)}
                      onChange={(e) => {
                        setWebhookEvents((prev) =>
                          e.target.checked ? [...prev, ev] : prev.filter((x) => x !== ev),
                        );
                      }}
                    />{' '}
                    {WEBHOOK_EVENT_LABELS[ev]}
                  </label>
                ))}
              </Field>
              <Button type="submit">Webhook toevoegen</Button>
            </form>
            {webhookSecret ? (
              <p className={styles.mutedSmall}>
                Signing secret (HMAC): <code>{webhookSecret}</code>
              </p>
            ) : null}
            <ul className={styles.list}>
              {(hooksQ.data?.webhooks ?? []).map((w) => (
                <li key={w.id} className={styles.listItem}>
                  {w.url} — {w.events.join(', ')}
                  <Button variant="ghost" onClick={() => testHook.mutate(w.id)}>
                    Test
                  </Button>
                </li>
              ))}
            </ul>
          </section>

          <section className={styles.card}>
            <h2>Delivery log</h2>
            {deliveriesQ.isLoading ? (
              <LoadingState />
            ) : (
              <ul className={styles.list}>
                {(deliveriesQ.data?.deliveries ?? []).slice(0, 50).map((d) => (
                  <li key={d.id} className={styles.listItem}>
                    <div>
                      {new Date(d.created_at).toLocaleString('nl-NL')} · {d.event_type} · {d.status}
                      {d.response_status != null ? ` (${d.response_status})` : ''} · {d.attempts}×
                    </div>
                    <div className={styles.mutedSmall}>{d.webhook_url}</div>
                    {d.trace_id ? <TraceLink traceId={d.trace_id} /> : null}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className={styles.card}>
            <h2>Domein → project</h2>
            <form
              className={styles.formGrid}
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                addDomain.mutate();
              }}
            >
              <Field label="Domein">
                <TextInput
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="www.voorbeeld.nl"
                  required
                />
              </Field>
              <Button type="submit">Koppelen</Button>
            </form>
            <ul className={styles.list}>
              {(domainsQ.data?.domains ?? []).map((d) => (
                <li key={d.id}>{d.domain_normalized}</li>
              ))}
            </ul>
          </section>
        </div>
      )}
    </ProjectGate>
  );
}
